// =============================================
// TokenCheckDialog — "Sprawdz token" dla konta CF: na gorze 3 badge'e
// uprawnien (Zone:Read / DNS:Edit / Pages:Edit — ma / brak / nie do
// sprawdzenia), ponizej 7 krokow z komunikatami. Zastepuje zakladke "Test".
// =============================================
import { useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, CheckCircle2, XCircle, MinusCircle, RefreshCw } from "lucide-react";
import type { CloudflareAccount } from "@/hooks/useCloudflareAccounts";
import type { TokenCheckPermissions, TokenCheckStatus, TokenCheckStep } from "@/services/cloudflare/tokenCheck";
import { TEST_TXT_NAME } from "@/services/cloudflare/tokenCheck";
import { useTokenCheck } from "./useTokenCheck";

const stepIcon = (s: TokenCheckStatus) =>
  s === "running" ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
    : s === "ok" ? <CheckCircle2 className="h-4 w-4 text-emerald-500" />
      : s === "fail" ? <XCircle className="h-4 w-4 text-red-500" />
        : <MinusCircle className="h-4 w-4 text-muted-foreground" />;

/** 3 badge'e uprawnien — reuse w dialogu edycji konta. */
export const PermissionBadges = ({ perms, pending }: { perms: TokenCheckPermissions | null; pending?: boolean }) => {
  const item = (label: string, v: boolean | null | undefined) => {
    if (pending || perms === null) return <Badge key={label} variant="outline" className="text-xs">{label} · …</Badge>;
    if (v === true) return <Badge key={label} variant="outline" className="text-xs border-emerald-500/40 text-emerald-400 bg-emerald-500/10">{label} ✓ ma</Badge>;
    if (v === false) return <Badge key={label} variant="outline" className="text-xs border-red-500/40 text-red-400 bg-red-500/10">{label} ✗ brak</Badge>;
    return <Badge key={label} variant="outline" className="text-xs text-muted-foreground" title="Brak projektu Pages — zapis sprawdzi się przy pierwszym deployu">{label} · nie do sprawdzenia</Badge>;
  };
  return (
    <div className="flex flex-wrap gap-1.5">
      {item("Zone:Read", perms?.zoneRead)}
      {item("DNS:Edit", perms?.dnsEdit)}
      {item("Pages:Edit", perms?.pagesEdit)}
    </div>
  );
};

export const TokenCheckSteps = ({ steps }: { steps: TokenCheckStep[] }) => (
  <div className="divide-y rounded-lg border">
    {steps.map((r) => (
      <div key={r.id} className="flex items-start gap-3 px-3 py-2.5">
        <div className="mt-0.5">{stepIcon(r.status)}</div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium">{r.label}</div>
          <div className="text-xs text-muted-foreground">{r.what}</div>
          {r.message && <div className={`mt-1 break-words text-xs ${r.status === "fail" ? "text-red-500" : "text-muted-foreground"}`}>{r.message}</div>}
        </div>
      </div>
    ))}
  </div>
);

interface Props {
  account: CloudflareAccount | null;
  onClose: () => void;
}

export const TokenCheckDialog = ({ account, onClose }: Props) => {
  const { steps, summary, running, run, reset } = useTokenCheck();

  useEffect(() => {
    if (account) void run(account);
    else reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.id]);

  return (
    <Dialog open={Boolean(account)} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Sprawdź token — {account?.label}</DialogTitle>
          <DialogDescription>
            Odpala każdą funkcję, której panel używa z tym kontem. Test zapisu DNS tworzy i od razu kasuje rekord TXT <span className="font-mono">{TEST_TXT_NAME}</span> na pierwszej domenie alfabetycznie — nic więcej nie zmienia.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <PermissionBadges perms={summary?.permissions ?? null} pending={running} />
          {steps && <TokenCheckSteps steps={steps} />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => account && void run(account)} disabled={running || !account}>
            {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}Powtórz
          </Button>
          <Button onClick={onClose}>Zamknij</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
